import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Clarity from '@microsoft/clarity';
import { useApi, configApiRef, identityApiRef } from '@backstage/core-plugin-api';

const PAGE_ID_MAP: Record<string, string> = {
  '/catalog': 'catalog',
  '/docs': 'techdocs',
  '/create': 'scaffolder',
  '/search': 'search',
  '/api-docs': 'api-docs',
  '/dashboard': 'dashboard',
  '/welcome': 'welcome',
  '/tech-radar': 'tech-radar',
  '/settings': 'settings',
  '/notifications': 'notifications',
  '/qeta': 'qeta',
  '/my-work': 'my-work',
  '/tools-resources': 'tools-resources',
  '/explore': 'explore',
};

const getPageId = (pathname: string): string => {
  for (const [prefix, pageId] of Object.entries(PAGE_ID_MAP)) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      return pageId;
    }
  }
  return 'other';
};

/**
 * Initializes Microsoft Clarity session tracking and identifies the user
 * on each page navigation.
 */
export const useClarity = () => {
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const location = useLocation();
  const [initialized, setInitialized] = useState(false);

  const projectId = config.getOptionalString('app.microsoftClarity.projectId');

  // Initialize Clarity once
  useEffect(() => {
    if (!projectId || initialized) return;

    try {
      Clarity.init(projectId);
      setInitialized(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[Clarity] Failed to initialize:', error);
    }
  }, [projectId, initialized]);

  // Stable identify function
  const identify = useCallback(async (pathname: string) => {
    const pageId = getPageId(pathname);
    try {
      const identity = await identityApi.getBackstageIdentity();
      const userId = identity?.userEntityRef ?? 'anonymous';
      Clarity.identify(userId, undefined, pageId, userId);
    } catch {
      Clarity.identify('anonymous', undefined, pageId);
    }
  }, [identityApi]);

  // Identify on navigation and initial load
  useEffect(() => {
    if (!initialized) return;

    identify(location.pathname).catch(() => {
      // silently ignore — Clarity is non-critical
    });
  }, [initialized, location.pathname, identify]);
};